import { useTheme } from '@automata/mui-theme';
import { Brush as VisxBrush } from '@visx/brush';
import BaseBrush from '@visx/brush/lib/BaseBrush';
import { PartialBrushStartEnd } from '@visx/brush/lib/types';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from 'd3-scale';
import { useRef } from 'react';

import BrushHandle from './BrushHandle';
import { PATTERN_1_ID } from '../Pattern1';

const isString = (input: unknown): input is string => typeof input === 'string';

export type Props = {
  width: number;
  height: number;
  domain: string[];
  x: number;
  y: number;
  onChange: (yValues: string[]) => void;
};

const BrushY = ({
  width,
  height,
  domain,
  x,
  y,
  onChange,
}: Props): JSX.Element | null => {
  const innerRef = useRef<BaseBrush>(null);
  const theme = useTheme();

  if (height < 10) return null;

  const offsetHeight = height - y;

  const scaleX = scaleLinear([1, 2], [x, x + width]);

  const scaleYRange: [number, number] = [0, offsetHeight];
  const scaleY = scaleBand(domain, scaleYRange);

  const paddingAmount = 40;
  const scaleYPadded = scaleY
    .copy()
    .range([scaleYRange[0] + paddingAmount, scaleYRange[1] - paddingAmount]);

  const initialBrushPosition: PartialBrushStartEnd = {
    start: { y: 0 },
    end: { y: scaleYPadded.range()[1] },
  };

  const paddedWidth = width * 0.8;

  const styledBoxStrokeWidth = 2;

  const handleReset = () => {
    onChange(scaleY.domain());
  };

  return (
    <Group top={y} left={x}>
      <rect
        width={Math.max(width, 0)}
        height={Math.max(offsetHeight, 0)}
        fill={theme.palette.neutral[200]}
      />

      <Group top={paddingAmount / 2} left={(width - paddedWidth) / 2}>
        <VisxBrush
          innerRef={innerRef}
          brushDirection="vertical"
          resizeTriggerAreas={['top', 'bottom']}
          height={scaleYPadded.range()[1]}
          width={paddedWidth}
          yScale={scaleYPadded}
          xScale={scaleX}
          onChange={(bounds) => {
            if (!bounds?.yValues) return;
            const yValues: string[] = bounds.yValues.filter(isString);
            onChange(yValues);
          }}
          useWindowMoveEvents
          renderBrushHandle={(props) => (
            <BrushHandle
              {...props}
              width={width - styledBoxStrokeWidth / 2}
              x={(width - paddedWidth) / -2 + styledBoxStrokeWidth / 4}
            />
          )}
          selectedBoxStyle={{
            stroke: theme.palette.grey[700],
            strokeWidth: styledBoxStrokeWidth,
            fill: `url(#${PATTERN_1_ID})`,
            fillOpacity: 0.2,
          }}
          onClick={handleReset}
          initialBrushPosition={initialBrushPosition}
          handleSize={30}
        />
      </Group>
    </Group>
  );
};

export default BrushY;
